/**
 * PETALS — the premium in-game currency, and its real-money value.
 *
 * Petals are the Shelf Garden currency EARNED on the spin wheel in the Shopify app (spend shards/tokens to
 * spin, win Petals). They are not (currently) bought directly, so to price a real-money product "in Petals"
 * we derive the average dollar value of a Petal from the actual wheel economics:
 *
 *   • SHARDS_PER_CENT = 7          — the app's `shards_per_cent` setting (7 shards ≡ 1¢ of retail value)
 *   • SPIN_COST_SHARDS = 100       — the app's default `wheel_spin_cost` (shards spent per spin)
 *   • WHEEL_SEGMENTS               — the live wheel: each outcome's Petals payout + probability weight
 *
 *   spin cost ($)      = SPIN_COST_SHARDS / SHARDS_PER_CENT / 100
 *   avg Petals / spin  = Σ(petals · weight) / Σ(weight)
 *   value of 1 Petal   = spin cost ($) / avg Petals per spin
 *
 * With the current wheel this works out to ≈ 98.5 Petals/spin and ≈ $0.00145 per Petal, so $2.99 ≈ 2062
 * Petals. If the wheel or shard rate changes, update the constants below and every price re-derives.
 *
 * WHAT THIS FILE OWNS, AND WHAT IT DOES NOT. Everything here is the exchange RATE — what a Petal is worth in
 * dollars, and therefore what a product costs in Petals. It has no opinion about any player's balance.
 *
 * THE BALANCE IS REAL NOW (this header used to say it was not). Petals are held in the save as `petals` and
 * EARNED through exactly one function — `grantPetals` in App.tsx — which every source routes through: the
 * daily check-in claim, achievement payouts and point-track payouts today, and anything added later. That
 * funnel exists because the monthly subscription pays 3× Petals (sim/subscription.ts): with the multiplier
 * at the single point of entry, a new Petals source gets the benefit automatically, whereas multiplying at
 * each call site guarantees the next one added silently pays 1×. If you are adding a way to earn Petals,
 * call `grantPetals` — do not call `setPetals`, which is for spending and for the dev cheat.
 *
 * The rate below is deliberately untouched by any of that: a subscriber earns Petals three times as fast,
 * and a Petal is still worth exactly what the wheel says it is worth.
 *
 * SPENDING IS SERVER-SIDE. The shop cards and the product detail modal print "$2.99 or 2,062 Petals" off
 * `productPetalsPrice` beside a "Buy with Petals" control (ui/site/PetalsBuy.tsx), and the purchase is
 * POST /api/shop/buy-with-petals: the server debits the cloud save and issues the same entitlement Stripe's
 * webhook does, because a client-side debit is just a number in localStorage. The server charges its own
 * copy of this rate (server/petals.js, plain JS — node cannot import this file); petals.test.ts fails if the
 * two drift. A running game takes the spend off its balance via `setPetals` when the shop announces it
 * (App.tsx, PETALS_SPENT_CHANNEL) — the only in-game spend path.
 */

/** Shards equivalent to 1¢ of retail value (Shopify `shards_per_cent`). */
export const SHARDS_PER_CENT = 7;
/** Shards spent to spin the wheel once (Shopify `wheel_spin_cost` default). */
export const SPIN_COST_SHARDS = 100;

/** The live wheel: each active segment's Petals payout + relative weight (odds = weight / Σweight). */
export const WHEEL_SEGMENTS: Array<{ petals: number; weight: number }> = [
  { petals: 300, weight: 3 },
  { petals: 220, weight: 5 },
  { petals: 160, weight: 8 },
  { petals: 120, weight: 12 },
  { petals: 95, weight: 17 },
  { petals: 80, weight: 22 },
  { petals: 60, weight: 18 },
  { petals: 45, weight: 15 },
];

/** Average Petals won per spin (probability-weighted). */
export const AVG_PETALS_PER_SPIN = (() => {
  const w = WHEEL_SEGMENTS.reduce((a, s) => a + s.weight, 0) || 1;
  return WHEEL_SEGMENTS.reduce((a, s) => a + s.petals * s.weight, 0) / w;
})();

/** Dollar cost of one spin. */
export const SPIN_COST_USD = SPIN_COST_SHARDS / SHARDS_PER_CENT / 100;

/** Average real-money value of ONE Petal, in dollars (≈ $0.00145 with the current wheel). */
export const PETAL_VALUE_USD = SPIN_COST_USD / AVG_PETALS_PER_SPIN;

/** Convert a USD amount (in CENTS) to the equivalent number of Petals, rounded to a whole Petal. */
export function centsToPetals(cents: number): number {
  return Math.round((cents / 100) / PETAL_VALUE_USD);
}

/**
 * The Petals price to show for a product: an explicit per-product override (metadata.petalsPrice) if set,
 * otherwise the amount derived from its fiat price via the wheel economics. Returns null if the product is
 * free or has no fiat price.
 */
export function productPetalsPrice(p: { priceCents: number; metadata?: Record<string, unknown> }): number | null {
  const override = p.metadata?.["petalsPrice"];
  if (typeof override === "number" && override > 0) return Math.round(override);
  if (!p.priceCents || p.priceCents <= 0) return null;
  return centsToPetals(p.priceCents);
}
